import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Cascader,
  Space,
  message,
  Tabs,
  Tag,
  Divider,
  InputNumber,
  Row,
  Col,
  Grid,
  Typography,
  Segmented,
  Descriptions,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MergeOutlined,
  SafetyCertificateOutlined,
  FileAddOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import api from '../../api/http';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

// 截图：危险源分类（内置）
const HAZARD_SOURCE_CATEGORY_OPTIONS = [
  {
    value: '人的因素',
    label: '第一类：人的因素',
    children: [
      {
        value: '心理/生理性危险',
        label: '1.心理/生理性危险',
        children: [{ value: '能力与状态不足', label: '能力与状态不足' }],
      },
      {
        value: '行为性危险',
        label: '2.行为性危险',
        children: [{ value: '不安全动作', label: '不安全动作' }],
      },
    ],
  },
  {
    value: '物的因素',
    label: '第二类：物的因素',
    children: [
      {
        value: '物理性危险',
        label: '1.物理性危险',
        children: [{ value: '能量与物理属性', label: '能量与物理属性' }],
      },
      {
        value: '化学性危险',
        label: '2.化学性危险',
        children: [{ value: '有毒化学物质', label: '有毒化学物质' }],
      },
      {
        value: '生物性危险',
        label: '3.生物性危险',
        children: [{ value: '致病微生物', label: '致病微生物' }],
      },
    ],
  },
  {
    value: '环境因素',
    label: '第三类：环境因素',
    children: [
      {
        value: '作业场所环境',
        label: '1.作业场所环境',
        children: [{ value: '物理环境不良', label: '物理环境不良' }],
      },
      {
        value: '外部自然环境',
        label: '2.外部自然环境',
        children: [{ value: '不可抗力影响', label: '不可抗力影响' }],
      },
    ],
  },
  {
    value: '管理因素',
    label: '第四类：管理因素',
    children: [
      {
        value: '体系与制度缺陷',
        label: '1.体系与制度缺陷',
        children: [{ value: '系统根源缺陷', label: '系统根源缺陷' }],
      },
      {
        value: '组织与协调缺陷',
        label: '2.组织与协调缺陷',
        children: [{ value: '资源与协调问题', label: '资源与协调问题' }],
      },
    ],
  },
];

const joinCategoryPath = (arr) => (Array.isArray(arr) && arr.length ? arr.join(' / ') : null);
const splitCategoryPath = (s) => (s ? String(s).split(' / ').map((x) => x.trim()).filter(Boolean) : []);

const MEASURE_TYPE_LABELS = {
  'ELIMINATION': '消除',
  'SUBSTITUTION': '替代',
  'ENGINEERING': '工程控制',
  'ADMINISTRATIVE': '管理控制',
  'PPE': '个体防护'
};

const MEASURE_TYPE_OPTIONS = [
  { value: 'ELIMINATION', label: '1. 消除 (Elimination)' },
  { value: 'SUBSTITUTION', label: '2. 替代 (Substitution)' },
  { value: 'ENGINEERING', label: '3. 工程控制 (Engineering Controls)' },
  { value: 'ADMINISTRATIVE', label: '4. 管理控制 (Administrative Controls)' },
  { value: 'PPE', label: '5. 个体防护 (PPE)' },
];

// 截图：工具选择速查决策表
const TOOL_MATRIX = [
  {
    key: 'scene_sop_train',
    scene: '制定安全操作规程或培训员工',
    primary_code: 'JSA',
    primary_name: '工作安全分析',
    aux_name: '类比与回顾法',
    output: '《JSA分析表》、标准作业程序',
  },
  {
    key: 'scene_maintenance_loto',
    scene: '确保检修作业安全，防止意外启动',
    primary_code: 'ENERGY_ISOLATION',
    primary_name: '能量隔离分析',
    aux_name: '工作安全分析',
    output: '《能量隔离清单》、LOTO程序',
  },
  {
    key: 'scene_compliance_quick',
    scene: '合规性检查或快速隐患排查',
    primary_code: 'CHECKLIST',
    primary_name: '安全检查表法',
    aux_name: null,
    output: '《安全检查记录表》',
  },
  {
    key: 'scene_process_new_design',
    scene: '分析复杂化工工艺或新项目设计',
    primary_code: 'HAZOP',
    primary_name: 'HAZOP（工艺危害分析）',
    aux_name: 'FMEA',
    output: '《HAZOP分析报告》、建议措施清单',
  },
  {
    key: 'scene_major_accident',
    scene: '重大事故风险沟通与屏障评估',
    primary_code: 'BOW_TIE',
    primary_name: 'Bow-Tie分析',
    aux_name: null,
    output: 'Bow-Tie风险图谱',
  },
  {
    key: 'scene_moc',
    scene: '评估“四新”（新工艺、设备等）引入的风险',
    primary_code: 'MOC',
    primary_name: '变更管理分析',
    aux_name: 'HAZOP或JSA',
    output: '《变更风险评估表》',
  },
  {
    key: 'scene_incident_review',
    scene: '事故复盘或预防类似事件',
    primary_code: 'RETROSPECT',
    primary_name: '类比与回顾法',
    aux_name: 'Bow-Tie分析',
    output: '《事故教训与风险警示》',
  },
];

const unwrapList = (resp) => {
  const data = resp?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const riskLevelTag = (level) => {
  const s = String(level || '').toUpperCase();
  if (!s) return <Tag>—</Tag>;
  if (s === 'HIGH' || s === '高') return <Tag color="red">高风险</Tag>;
  if (s === 'MEDIUM' || s === '中') return <Tag color="orange">中风险</Tag>;
  return <Tag color="green">低风险</Tag>;
};

const computeMatrixRisk = (likelihood, loss) => {
  const l = Number.parseInt(String(likelihood || ''), 10);
  const s = Number.parseInt(String(loss || ''), 10);
  if (!Number.isFinite(l) || !Number.isFinite(s)) return { riskValue: null, riskLevel: null };
  if (l < 1 || l > 5 || s < 1 || s > 5) return { riskValue: null, riskLevel: null };
  const riskValue = l * s;
  let riskLevel = 'LOW';
  if (riskValue >= 16) riskLevel = 'HIGH';
  else if (riskValue >= 6) riskLevel = 'MEDIUM';
  return { riskValue, riskLevel };
};

export default function RiskIdentificationPage() {
  const screens = Grid.useBreakpoint();
  const isMobile = !!screens.xs && !screens.sm;
  const tableScrollX = isMobile ? { x: 'max-content' } : undefined;
  const modalWidthMd = isMobile ? '96%' : 700;
  const modalWidthLg = isMobile ? '96%' : 800;
  const auth = useAuth();
  const isManager = auth?.hasPermission?.(['hazard_manage']);

  const [activeTab, setActiveTab] = useState('methods');

  const [categoryList, setCategoryList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryEditing, setCategoryEditing] = useState(null);
  const [categoryForm] = Form.useForm();

  const [riskPoints, setRiskPoints] = useState([]);
  const [riskPointsLoading, setRiskPointsLoading] = useState(false);
  const [selectedRiskPointId, setSelectedRiskPointId] = useState(null);
  const [riskPointEditing, setRiskPointEditing] = useState(null);

  const [createRiskModalOpen, setCreateRiskModalOpen] = useState(false);
  const [createRiskForm] = Form.useForm();

  const [hazardSources, setHazardSources] = useState([]);
  const [hazardSourcesLoading, setHazardSourcesLoading] = useState(false);
  const [selectedHazardSourceId, setSelectedHazardSourceId] = useState(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemEditing, setItemEditing] = useState(null);
  const [itemForm] = Form.useForm();

  const [summaryScope, setSummaryScope] = useState('unarchived'); // unarchived | archived
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedSummaryRowKeys, setSelectedSummaryRowKeys] = useState([]);

  // 防止多次“刷新”触发并发请求导致 loading 闪烁：只让最后一次请求落地
  const summaryReqSeqRef = useRef(0);

  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeForm] = Form.useForm();

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('APPROVE');
  const [reviewForm] = Form.useForm();

  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [gradeForm] = Form.useForm();

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveForm] = Form.useForm();

  const [measuresList, setMeasuresList] = useState([]);
  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measureForm] = Form.useForm();

  // Tab-level permission control (if any granular perms are configured for current user)
  const TAB_PERMS = useMemo(
    () => ({
      methods: 'hazard_risk_identification_methods',
      mine: 'hazard_risk_identification_mine',
      summary: 'hazard_risk_identification_summary',
      categories: 'hazard_risk_identification_categories',
    }),
    []
  );
  const hasAnyTabPerm = useMemo(() => {
    const names = Object.values(TAB_PERMS);
    return names.some((p) => auth?.hasPermission?.([p]));
  }, [TAB_PERMS, auth]);
  const canShowTab = useCallback(
    (key) => {
      if (!hasAnyTabPerm) return true;
      const perm = TAB_PERMS[key];
      if (!perm) return true;
      return !!auth?.hasPermission?.([perm]);
    },
    [TAB_PERMS, auth, hasAnyTabPerm]
  );

  const visibleTabKeys = useMemo(
    () => ['methods', 'mine', 'summary', 'categories'].filter((k) => canShowTab(k)),
    [canShowTab]
  );

  useEffect(() => {
    if (!visibleTabKeys.length) return;
    if (!visibleTabKeys.includes(activeTab)) {
      setActiveTab(visibleTabKeys[0]);
    }
  }, [activeTab, visibleTabKeys]);

  const categoryOptions = useMemo(
    () => (categoryList || []).filter((c) => Number(c?.is_active) === 1).map((c) => ({ label: c.name, value: c.id })),
    [categoryList]
  );
  
  const measureOptions = useMemo(() => {
     // Group by measure_type if possible, but AntD Select options with grouping is complex with tags mode sometimes.
     // Let's just return flat list with type info in label? Or just name.
     return (measuresList || []).map(m => ({
        label: `${m.name} [${MEASURE_TYPE_LABELS[m.measure_type] || '通用'}]`,
        value: m.name,
        desc: m.description, // for potential use
        type: m.measure_type
     }));
  }, [measuresList]);

  const loadTreatmentMeasures = useCallback(async () => {
    try {
      const resp = await api.get('/risk-management/treatment-measures');
      setMeasuresList(unwrapList(resp));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadTreatmentMeasures();
  }, [loadTreatmentMeasures]);

  const loadCategories = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const resp = await api.get('/risk-management/risk-identification/categories', { params: { active: 1 } });
      setCategoryList(unwrapList(resp));
    } catch (e) {
      message.error('获取自定义类别失败');
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  const loadMyRiskPoints = useCallback(async () => {
    setRiskPointsLoading(true);
    try {
      const resp = await api.get('/risk-management/risk-identification/risk-points', { params: { mine: 1 } });
      const list = unwrapList(resp);
      setRiskPoints(list);
      
      setSelectedRiskPointId((prev) => {
        if (!prev && list.length) return list[0].id;
        if (prev && list.length && !list.some((r) => Number(r.id) === Number(prev))) {
          return list[0].id;
        }
        if (!list.length) return null;
        return prev;
      });
    } catch (e) {
      message.error('获取我的风险点失败');
    } finally {
      setRiskPointsLoading(false);
    }
  }, []);

  const loadHazardSources = useCallback(
    async (riskPointId) => {
      if (!riskPointId) {
        setHazardSources([]);
        setSelectedHazardSourceId(null);
        return;
      }
      setHazardSourcesLoading(true);
      try {
        const resp = await api.get('/risk-management/risk-identification/hazard-sources', {
          // 我的识别：展示所有状态（含已建档），以便显示状态并限制撤回
          params: { risk_point_id: riskPointId, mine: 1, include_merged: 0 },
        });
        const list = unwrapList(resp);
        setHazardSources(list);

        setSelectedHazardSourceId((prev) => {
           if (!prev && list.length) return list[0].id;
           if (prev && list.length && !list.some((r) => Number(r.id) === Number(prev))) {
             return list[0].id;
           }
           if (!list.length) return null;
           return prev;
        });

      } catch (e) {
        message.error('获取危险源失败');
      } finally {
        setHazardSourcesLoading(false);
      }
    },
    []
  );

  const loadSummary = useCallback(async () => {
    const reqId = ++summaryReqSeqRef.current;
    setSummaryLoading(true);
    try {
      const resp = await api.get('/risk-management/risk-identification/hazard-sources', {
        params: {
          // 清单汇总：仅展示已提交（非草稿）的危险源
          submitted_only: 1,
          include_merged: 0,
          archived: summaryScope === 'archived' ? 1 : 0,
        },
      });
      if (summaryReqSeqRef.current !== reqId) return;
      setSummaryRows(unwrapList(resp));
      setSelectedSummaryRowKeys([]);
    } catch (e) {
      if (summaryReqSeqRef.current !== reqId) return;
      message.error('获取清单汇总失败');
    } finally {
      if (summaryReqSeqRef.current === reqId) {
        setSummaryLoading(false);
      }
    }
  }, [summaryScope]);

  const selectedHazardSourceRow = useMemo(() => {
    if (!selectedHazardSourceId) return null;
    return (hazardSources || []).find((x) => Number(x.id) === Number(selectedHazardSourceId)) || null;
  }, [hazardSources, selectedHazardSourceId]);

  const submitHazardSource = async () => {
    const row = selectedHazardSourceRow;
    if (!row) return;
    Modal.confirm({
      title: '提交该危险源？',
      content: '提交后将出现在“清单汇总”中，等待管理员评审。',
      okText: '提交',
      onOk: async () => {
        try {
          await api.post(`/risk-management/risk-identification/hazard-sources/${row.id}/submit`);
          message.success('已提交');
          await loadHazardSources(selectedRiskPointId);
        } catch (e) {
          message.error(e?.response?.data?.error || '提交失败');
        }
      },
    });
  };

  const withdrawHazardSource = async () => {
    const row = selectedHazardSourceRow;
    if (!row) return;
    Modal.confirm({
      title: '撤回该危险源？',
      content: '撤回后将从“清单汇总”中移除，并恢复为草稿。',
      okText: '撤回',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.post(`/risk-management/risk-identification/hazard-sources/${row.id}/withdraw`);
          message.success('已撤回');
          await loadHazardSources(selectedRiskPointId);
        } catch (e) {
          message.error(e?.response?.data?.error || '撤回失败');
        }
      },
    });
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'mine') {
      loadMyRiskPoints();
    }
  }, [activeTab, loadMyRiskPoints]);

  useEffect(() => {
    if (activeTab === 'mine') {
      loadHazardSources(selectedRiskPointId);
    }
  }, [activeTab, loadHazardSources, selectedRiskPointId]);

  useEffect(() => {
    if (activeTab === 'summary') {
      loadSummary();
    }
  }, [activeTab, loadSummary]);

  const openCreateCategory = () => {
    setCategoryEditing(null);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({ is_active: 1, sort_order: 0 });
    setCategoryModalOpen(true);
  };

  const openEditCategory = (row) => {
    setCategoryEditing(row);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({
      name: row?.name,
      code: row?.code,
      description: row?.description,
      sort_order: row?.sort_order ?? 0,
      is_active: row?.is_active ?? 1,
    });
    setCategoryModalOpen(true);
  };

  const submitCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      if (categoryEditing?.id) {
        await api.put(`/risk-management/risk-identification/categories/${categoryEditing.id}`, values);
        message.success('类别已更新');
      } else {
        await api.post('/risk-management/risk-identification/categories', values);
        message.success('类别已创建');
      }
      setCategoryModalOpen(false);
      await loadCategories();
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err) message.error(err);
    }
  };

  const deleteCategory = async (row) => {
    Modal.confirm({
      title: '停用该类别？',
      content: row?.name,
      okText: '停用',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/risk-management/risk-identification/categories/${row.id}`);
          message.success('已停用');
          await loadCategories();
        } catch (e) {
          message.error('停用失败');
        }
      },
    });
  };

  const openCreateRiskPoint = () => {
    setRiskPointEditing(null);
    createRiskForm.resetFields();
    createRiskForm.setFieldsValue({
      department: auth?.user?.department || '',
      identified_by: auth?.user?.name || auth?.user?.username,
      identification_date: dayjs(),
    });
    setCreateRiskModalOpen(true);
  };

  const openEditRiskPoint = (row) => {
    setRiskPointEditing(row);
    // 尝试解析 identification_methods
    let methods = row?.identification_methods;
    if (typeof methods === 'string') {
      try { methods = JSON.parse(methods); } catch(e) {}
    }
    
    createRiskForm.resetFields();
    createRiskForm.setFieldsValue({
      risk_point_name: row?.risk_point_name,
      department: row?.department,
      identified_by: row?.identified_by,
      identification_date: row?.identification_date ? dayjs(row.identification_date) : null,
      identification_methods: methods,
    });
    setCreateRiskModalOpen(true);
  };

  const submitCreateRiskPoint = async () => {
    try {
      const values = await createRiskForm.validateFields();
      
      const payload = {
        risk_point_name: String(values.risk_point_name || '').trim(),
        department: String(values.department || '').trim(),
        identification_methods: values.identification_methods, 
        identified_by: String(values.identified_by || '').trim(),
        identification_date: values.identification_date ? values.identification_date.format('YYYY-MM-DD') : null,
      };

      if (riskPointEditing?.id) {
         await api.put(`/risk-management/risk-identification/risk-points/${riskPointEditing.id}`, payload);
         message.success('风险点已更新');
         setCreateRiskModalOpen(false);
         createRiskForm.resetFields();
         await loadMyRiskPoints();
      } else {
         const resp = await api.post('/risk-management/risk-identification/risk-points', payload);
         const newId = resp?.data?.id || resp?.data?.data?.id;

         message.success('风险点已保存');
         setCreateRiskModalOpen(false);
         createRiskForm.resetFields();

         await loadMyRiskPoints();
         if (newId) {
            setSelectedRiskPointId(newId);
            await loadHazardSources(newId);
         }
      }
    } catch (e) {
      console.error(e);
      const err = e?.response?.data?.error;
      if (err) message.error(err);
      else message.error(e?.message || '保存失败');
    }
  };

  const deleteRiskPoint = async () => {
    if (!selectedRiskPointId) return;
    Modal.confirm({
      title: '删除风险点？',
      content: '删除后无法恢复，且会同步删除该风险点下的所有危险源。',
      okText: '删除',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/risk-management/risk-identification/risk-points/${selectedRiskPointId}`);
          message.success('已删除');
          await loadMyRiskPoints();
          setSelectedRiskPointId(null);
          setHazardSources([]);
        } catch (e) {
           message.error(e?.response?.data?.error || '删除失败');
        }
      },
    });
  };

  const deleteHazardSource = async () => {
     if (!selectedHazardSourceId) return;
     Modal.confirm({
      title: '删除危险源？',
      content: '确定要删除选中的危险源吗？',
      okText: '删除',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/risk-management/risk-identification/hazard-sources/${selectedHazardSourceId}`);
          message.success('已删除');
          await loadHazardSources(selectedRiskPointId);
          setSelectedHazardSourceId(null);
        } catch (e) {
           message.error(e?.response?.data?.error || '删除失败');
        }
      },
    });
  };

  const openCreateHazardSource = () => {
    if (!selectedRiskPointId) {
      message.warning('请先选择一个风险点');
      return;
    }
    setItemEditing(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({ risk_point_id: selectedRiskPointId, category_id: null });
    setItemModalOpen(true);
  };

  const openEditItem = (row) => {
    setItemEditing(row);
    itemForm.resetFields();
    
    // Parse existing string to array for Select[mode=tags]
    const measures = row?.control_measures ? String(row.control_measures).split('\n').filter(Boolean) : [];

    itemForm.setFieldsValue({
      risk_point_id: row?.risk_point_id,
      category_id: row?.category_id || null,
      hazard_source_category: splitCategoryPath(row?.hazard_source_category_path),
      hazard_source_name: row?.hazard_source_name,
      possible_consequence: row?.possible_consequence,
      control_measures: measures,
      likelihood_level: row?.likelihood_level,
      loss_level: row?.loss_level,
    });
    setItemModalOpen(true);
  };

  const submitItem = async () => {
    try {
      const values = await itemForm.validateFields();
      
      // Join array back to string
      const measuresStr = Array.isArray(values.control_measures) 
          ? values.control_measures.join('\n') 
          : (values.control_measures || '');

      const payload = {
        risk_point_id: values.risk_point_id,
        category_id: values.category_id || null,
        hazard_source_category_path: joinCategoryPath(values.hazard_source_category),
        hazard_source_name: values.hazard_source_name ? String(values.hazard_source_name).trim() : null,
        possible_consequence: values.possible_consequence ? String(values.possible_consequence).trim() : null,
        control_measures: measuresStr ? String(measuresStr).trim() : null,
        likelihood_level: values.likelihood_level,
        loss_level: values.loss_level,
      };

      if (itemEditing?.id) {
        await api.put(`/risk-management/risk-identification/hazard-sources/${itemEditing.id}`,
          {
            category_id: payload.category_id,
            hazard_source_category_path: payload.hazard_source_category_path,
            hazard_source_name: payload.hazard_source_name,
            possible_consequence: payload.possible_consequence,
            control_measures: payload.control_measures,
            likelihood_level: payload.likelihood_level,
            loss_level: payload.loss_level,
          }
        );
        message.success('危险源已更新');
      } else {
        await api.post('/risk-management/risk-identification/hazard-sources', payload);
        message.success('危险源已新增');
      }
      setItemModalOpen(false);
      await loadHazardSources(selectedRiskPointId);
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err) message.error(err);
    }
  };

  const selectedSummaryItems = useMemo(() => {
    const set = new Set(selectedSummaryRowKeys);
    return (summaryRows || []).filter((r) => set.has(r.id));
  }, [selectedSummaryRowKeys, summaryRows]);

  const openMerge = () => {
    if (!isManager) return message.warning('仅管理员可合并');
    if (selectedSummaryItems.length < 2) return message.warning('至少选择2条');

    const base = selectedSummaryItems[0];
    mergeForm.resetFields();
    mergeForm.setFieldsValue({
      risk_point_id: base?.risk_point_id,
      risk_point_name: base?.risk_point_name || '',
      category_id: base?.category_id || null,
      hazard_source_category: splitCategoryPath(base?.hazard_source_category_path),
      hazard_source_name: base?.hazard_source_name || '',
      possible_consequence: base?.possible_consequence || '',
      control_measures: base?.control_measures || '',
    });
    setMergeModalOpen(true);
  };

  const submitMerge = async () => {
    try {
      const values = await mergeForm.validateFields();
      await api.post('/risk-management/risk-identification/hazard-sources/merge', {
        hazard_source_ids: selectedSummaryRowKeys,
        merged: {
          risk_point_id: values.risk_point_id,
          category_id: values.category_id || null,
          hazard_source_category_path: joinCategoryPath(values.hazard_source_category),
          hazard_source_name: values.hazard_source_name ? String(values.hazard_source_name).trim() : null,
          possible_consequence: values.possible_consequence ? String(values.possible_consequence).trim() : null,
          control_measures: values.control_measures ? String(values.control_measures).trim() : null,
        },
      });
      message.success('已合并');
      setMergeModalOpen(false);
      await loadSummary();
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err) message.error(err);
    }
  };

  const openReview = (action) => {
    if (!isManager) return message.warning('仅管理员可评审');
    if (!selectedSummaryItems.length) return message.warning('请选择危险源');
    setReviewAction(action);
    reviewForm.resetFields();
    setReviewModalOpen(true);
  };

  const submitReview = async () => {
    try {
      const values = await reviewForm.validateFields();
      await api.post('/risk-management/risk-identification/hazard-sources/review', {
        hazard_source_ids: selectedSummaryRowKeys,
        action: reviewAction,
        comment: values.comment,
      });
      message.success('评审已提交');
      setReviewModalOpen(false);
      await loadSummary();
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err) message.error(err);
    }
  };

  const openGrade = () => {
    if (!isManager) return message.warning('仅管理员可分级');
    if (!selectedSummaryItems.length) return message.warning('请选择危险源');
    gradeForm.resetFields();
    gradeForm.setFieldsValue({ likelihood_level: 3, loss_level: 3 });
    setGradeModalOpen(true);
  };

  const submitGrade = async () => {
    try {
      const values = await gradeForm.validateFields();
      await api.post('/risk-management/risk-identification/hazard-sources/grade', {
        hazard_source_ids: selectedSummaryRowKeys,
        likelihood_level: values.likelihood_level,
        loss_level: values.loss_level,
      });
      message.success('分级已完成');
      setGradeModalOpen(false);
      await loadSummary();
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err) message.error(err);
    }
  };

  const openArchive = () => {
    if (!isManager) return message.warning('仅管理员可建档');
    if (!selectedSummaryItems.length) return message.warning('请选择危险源');

    const bad = selectedSummaryItems.filter((r) => String(r.status || '').toUpperCase() !== 'APPROVED');
    if (bad.length) {
      message.warning('仅支持对“评审通过”的危险源建档');
      return;
    }
    archiveForm.resetFields();
    setArchiveModalOpen(true);
  };

  const submitArchive = async () => {
    try {
      const values = await archiveForm.validateFields();
      const resp = await api.post('/risk-management/risk-identification/hazard-sources/archive', {
        hazard_source_ids: selectedSummaryRowKeys,
        inspection_cycle: values.inspection_cycle,
        inspection_responsible: values.inspection_responsible,
      });
      const archived = resp?.data?.archived ?? 0;
      message.success(`已建档 ${archived} 条`);
      setArchiveModalOpen(false);
      await loadSummary();
    } catch (e) {
      const err = e?.response?.data?.error;
      if (err) message.error(err);
    }
  };

  const submitMeasure = async () => {
    try {
      const values = await measureForm.validateFields();
      await api.post('/risk-management/treatment-measures', values);
      message.success('管控措施已添加');
      measureForm.resetFields();
      await loadTreatmentMeasures();
    } catch (e) {
      message.error(e?.response?.data?.error || '添加失败');
    }
  };

  const toolMatrixColumns = [
    { title: '你的场景/需求', dataIndex: 'scene' },
    { title: '首选推荐工具', dataIndex: 'primary_name', width: 180 },
    { title: '辅助/组合工具', dataIndex: 'aux_name', width: 160, render: (v) => v || '—', responsive: ['md'] },
    { title: '核心产出', dataIndex: 'output', width: 260, responsive: ['md'] },
  ];

  const summaryColumns = [
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (v) => {
        const s = String(v || '').toUpperCase();
        if (s === 'APPROVED') return <Tag color="green">通过</Tag>;
        if (s === 'REJECTED') return <Tag color="red">驳回</Tag>;
        if (s === 'IN_REVIEW') return <Tag color="blue">待评审</Tag>;
        if (s === 'ARCHIVED') return <Tag color="gold">已建档</Tag>;
        if (s === 'MERGED') return <Tag>已合并</Tag>;
        return <Tag>草稿</Tag>;
      },
    },
    {
      title: '风险点',
      dataIndex: 'risk_point_name',
      width: 240,
      responsive: ['sm'],
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r?.risk_point_name || '—'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r?.risk_place_part || '—'}</Text>
        </Space>
      ),
    },
    {
      title: '危险源类别',
      dataIndex: 'hazard_source_category_path',
      width: 220,
      render: (v, r) => v || r?.category_name || '—',
      responsive: ['md'],
    },
    {
      title: '危险源名称',
      dataIndex: 'hazard_source_name',
    },
    {
      title: '识别工具',
      dataIndex: 'method_name',
      width: 160,
      render: (v) => v || '—',
      responsive: ['md'],
    },
    {
      title: '分级',
      key: 'grade',
      width: 160,
      responsive: ['md'],
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            可能性{r?.likelihood_level ?? '—'} · 损失{r?.loss_level ?? '—'} · 值{r?.risk_value ?? '—'}
          </Text>
          {riskLevelTag(r?.risk_level)}
        </Space>
      ),
    },
  ];

  const categoryColumns = [
    { title: '名称', dataIndex: 'name' },
    { title: '编码', dataIndex: 'code', width: 140, render: (v) => v || '—' },
    { title: '描述', dataIndex: 'description', render: (v) => v || '—', responsive: ['md'] },
    { title: '排序', dataIndex: 'sort_order', width: 90 },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditCategory(r)} disabled={!isManager}>
            编辑
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteCategory(r)} disabled={!isManager}>
            停用
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? 12 : 16 }}>
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            危险源识别
          </Space>
        }
      >
        {(() => {
          const tabItemsAll = [
            {
              key: 'methods',
              label: '识别方法',
              children: (
                <>
                  <Text type="secondary">根据场景选择识别工具（与截图一致）。</Text>
                  <Divider />
                  <Table
                    rowKey="key"
                    columns={toolMatrixColumns}
                    dataSource={TOOL_MATRIX}
                    pagination={false}
                    size="middle"
                    scroll={tableScrollX}
                  />
                </>
              ),
            },
            {
              key: 'mine',
              label: '我的识别',
              children: (
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={10}>
                    <Card
                      size="small"
                      title="风险点"
                      extra={
                        <Space>
                          <Button 
                            size="small" 
                            onClick={() => {
                              measureForm.resetFields();
                              setMeasureModalOpen(true);
                              loadTreatmentMeasures();
                            }}
                          >
                            制定管控措施
                          </Button>
                          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateRiskPoint}>
                            新增
                          </Button>
                          <Button 
                            size="small" 
                            icon={<EditOutlined />} 
                            disabled={!selectedRiskPointId} 
                            onClick={() => {
                               const row = riskPoints.find(r => r.id === selectedRiskPointId);
                               if (row) openEditRiskPoint(row);
                            }}
                          >
                            编辑
                          </Button>
                          <Button size="small" danger icon={<DeleteOutlined />} disabled={!selectedRiskPointId} onClick={deleteRiskPoint}>
                            删除
                          </Button>
                          <Button size="small" onClick={loadMyRiskPoints}>刷新</Button>
                        </Space>
                      }
                    >
                      <Table
                        rowKey="id"
                        size="small"
                        columns={[
                          { title: '风险点名称', dataIndex: 'risk_point_name' },
                          { title: '部门/科室', dataIndex: 'risk_place_part', width: 140, render: (v) => v || '—', responsive: ['md'] },
                        ]}
                        dataSource={riskPoints}
                        loading={riskPointsLoading}
                        pagination={{ pageSize: isMobile ? 6 : 8 }}
                        scroll={tableScrollX}
                        rowSelection={{
                          type: 'radio',
                          selectedRowKeys: selectedRiskPointId ? [selectedRiskPointId] : [],
                          onChange: (keys) => setSelectedRiskPointId(keys?.[0] || null),
                        }}
                        onRow={(record) => ({
                          onClick: () => setSelectedRiskPointId(record.id),
                        })}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={14}>
                    <Card
                      size="small"
                      title="危险源"
                      extra={
                        <Space>
                          <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!selectedRiskPointId} onClick={openCreateHazardSource}>
                            新增
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            ghost
                            disabled={(() => {
                              const s = String(selectedHazardSourceRow?.status || '').toUpperCase();
                              const archived = !!selectedHazardSourceRow?.archived_hazard_id || s === 'ARCHIVED';
                              return !selectedHazardSourceRow || archived || (s !== 'DRAFT' && s !== 'REJECTED');
                            })()}
                            onClick={submitHazardSource}
                          >
                            提交
                          </Button>
                          <Button
                            size="small"
                            danger
                            disabled={(() => {
                              const s = String(selectedHazardSourceRow?.status || '').toUpperCase();
                              const archived = !!selectedHazardSourceRow?.archived_hazard_id || s === 'ARCHIVED';
                              return !selectedHazardSourceRow || archived || s === 'DRAFT' || s === 'MERGED';
                            })()}
                            onClick={withdrawHazardSource}
                          >
                            撤回
                          </Button>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            disabled={!selectedHazardSourceId || !!selectedHazardSourceRow?.archived_hazard_id}
                            onClick={() => {
                              const row = (hazardSources || []).find((x) => Number(x.id) === Number(selectedHazardSourceId));
                              if (row) openEditItem(row);
                            }}
                          >
                            编辑
                          </Button>
                          <Button 
                             size="small" 
                             danger 
                             icon={<DeleteOutlined />} 
                             disabled={!selectedHazardSourceId || !!selectedHazardSourceRow?.archived_hazard_id} 
                             onClick={deleteHazardSource}
                          >
                             删除
                          </Button>
                          <Button size="small" disabled={!selectedRiskPointId} onClick={() => loadHazardSources(selectedRiskPointId)}>
                            刷新
                          </Button>
                        </Space>
                      }
                    >
                      {!selectedRiskPointId ? (
                        <Text type="secondary">请从左侧选择一个风险点。</Text>
                      ) : (
                        <>
                          <Table
                            rowKey="id"
                            size="small"
                            columns={[
                              {
                                title: '状态',
                                dataIndex: 'status',
                                width: 90,
                                render: (v) => {
                                  const s = String(v || '').toUpperCase();
                                  if (s === 'APPROVED') return <Tag color="green">通过</Tag>;
                                  if (s === 'REJECTED') return <Tag color="red">驳回</Tag>;
                                  if (s === 'IN_REVIEW') return <Tag color="blue">待评审</Tag>;
                                  if (s === 'ARCHIVED') return <Tag color="gold">已建档</Tag>;
                                  if (s === 'MERGED') return <Tag>已合并</Tag>;
                                  return <Tag>草稿</Tag>;
                                },
                              },
                              {
                                title: '类别',
                                dataIndex: 'hazard_source_category_path',
                                width: 200,
                                render: (v, r) => v || r?.category_name || '—',
                                responsive: ['md'],
                              },
                              { title: '危险源名称', dataIndex: 'hazard_source_name' },
                              {
                                title: '分级',
                                key: 'grade',
                                width: 160,
                                responsive: ['md'],
                                render: (_, r) => (
                                  <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      可能性{r?.likelihood_level ?? '—'} · 损失{r?.loss_level ?? '—'} · 值{r?.risk_value ?? '—'}
                                    </Text>
                                    {riskLevelTag(r?.risk_level)}
                                  </Space>
                                ),
                              },
                            ]}
                            dataSource={hazardSources}
                            loading={hazardSourcesLoading}
                            pagination={{ pageSize: 6 }}
                            scroll={tableScrollX}
                            rowSelection={{
                              type: 'radio',
                              selectedRowKeys: selectedHazardSourceId ? [selectedHazardSourceId] : [],
                              onChange: (keys) => setSelectedHazardSourceId(keys?.[0] || null),
                            }}
                            onRow={(record) => ({
                              onClick: () => setSelectedHazardSourceId(record.id),
                            })}
                          />

                          <Divider style={{ margin: '12px 0' }} />

                          {(() => {
                            const row = (hazardSources || []).find((x) => Number(x.id) === Number(selectedHazardSourceId)) || null;
                            if (!row) return <Text type="secondary">请从上方选择一个危险源。</Text>;
                            return (
                              <Descriptions
                                bordered
                                size="small"
                                column={1}
                                items={[
                                  { key: 'rp', label: '风险点', children: row?.risk_point_name || '—' },
                                  { key: 'tool', label: '识别工具', children: row?.method_name || '—' },
                                  {
                                    key: 'cat',
                                    label: '危险源类别',
                                    children: row?.hazard_source_category_path || row?.category_name || '—',
                                  },
                                  { key: 'name', label: '危险源名称', children: row?.hazard_source_name || '—' },
                                  { key: 'dept', label: '部门/科室', children: row?.risk_place_part || '—' },
                                  { key: 'cons', label: '可能后果', children: row?.possible_consequence || '—' },
                                  { key: 'meas', label: '控制措施', children: row?.control_measures || '—' },
                                ]}
                              />
                            );
                          })()}
                        </>
                      )}
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'summary',
              label: '清单汇总',
              children: (
                <>
                  <Space style={{ marginBottom: 12 }} wrap>
                    <Segmented
                      value={summaryScope}
                      onChange={setSummaryScope}
                      options={[
                        { label: '待建档/处理中', value: 'unarchived' },
                        { label: '已建档', value: 'archived' },
                      ]}
                    />
                    <Button onClick={loadSummary} loading={summaryLoading} disabled={summaryLoading}>
                      刷新
                    </Button>
                    {isManager && summaryScope === 'unarchived' && (
                      <>
                        <Button icon={<MergeOutlined />} onClick={openMerge} disabled={selectedSummaryRowKeys.length < 2}>
                          合并
                        </Button>
                        <Button onClick={() => openReview('APPROVE')} disabled={!selectedSummaryRowKeys.length}>
                          评审通过
                        </Button>
                        <Button danger onClick={() => openReview('REJECT')} disabled={!selectedSummaryRowKeys.length}>
                          评审驳回
                        </Button>
                        <Button icon={<CalculatorOutlined />} onClick={openGrade} disabled={!selectedSummaryRowKeys.length}>
                          分级
                        </Button>
                        <Button type="primary" icon={<FileAddOutlined />} onClick={openArchive} disabled={!selectedSummaryRowKeys.length}>
                          建档
                        </Button>
                      </>
                    )}
                    {!isManager && (
                      <Text type="secondary">（部分操作如合并/评审/分级/建档仅管理员可用）</Text>
                    )}
                  </Space>

                  <Table
                    rowKey="id"
                    columns={summaryColumns}
                    dataSource={summaryRows}
                    loading={{ spinning: summaryLoading, delay: 200 }}
                    pagination={{ pageSize: 10 }}
                    scroll={tableScrollX}
                    rowSelection={
                      summaryScope === 'unarchived'
                        ? {
                            selectedRowKeys: selectedSummaryRowKeys,
                            onChange: setSelectedSummaryRowKeys,
                          }
                        : undefined
                    }
                  />
                </>
              ),
            },
            {
              key: 'categories',
              label: '自定义类别（可选）',
              children: (
                <>
                  <Space style={{ marginBottom: 12 }} wrap>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateCategory} disabled={!isManager}>
                      新增类别
                    </Button>
                    <Button onClick={loadCategories}>刷新</Button>
                    {!isManager && <Text type="secondary">（仅管理员可维护类别）</Text>}
                  </Space>
                  <Table
                    rowKey="id"
                    columns={categoryColumns}
                    dataSource={categoryList}
                    loading={categoryLoading}
                    pagination={{ pageSize: 10 }}
                    scroll={tableScrollX}
                  />
                </>
              ),
            },
          ];

          const tabItems = tabItemsAll.filter((t) => visibleTabKeys.includes(t.key));
          if (!tabItems.length) {
            return <Text type="secondary">暂无权限查看该页面内容（请在角色权限中勾选对应 Tab 权限）。</Text>;
          }
          return (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
            />
          );
        })()}
      </Card>

      {/* 类别弹窗 */}
      <Modal
        title={categoryEditing?.id ? '编辑类别' : '新增类别'}
        open={categoryModalOpen}
        onCancel={() => setCategoryModalOpen(false)}
        onOk={submitCategory}
        okButtonProps={{ disabled: !isManager }}
        width={isMobile ? '96%' : undefined}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item name="name" label="类别名称" rules={[{ required: true, message: '请输入类别名称' }]}>
            <Input placeholder="如：设备设施/电气/消防/特种设备..." />
          </Form.Item>
          <Form.Item name="code" label="类别编码（可选）">
            <Input placeholder="如：ELECTRICAL" />
          </Form.Item>
          <Form.Item name="description" label="描述（可选）">
            <Input placeholder="补充说明" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="sort_order" label="排序">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="is_active" label="是否启用">
                <Select options={[{ label: '启用', value: 1 }, { label: '停用', value: 0 }]} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 新增/编辑风险点 (简化版) */}
      <Modal
        title={riskPointEditing ? '编辑风险点' : '新增风险点'}
        open={createRiskModalOpen}
        onCancel={() => {
          setCreateRiskModalOpen(false);
          createRiskForm.resetFields();
        }}
        onOk={submitCreateRiskPoint}
        okText="保存"
        width={modalWidthMd}
      >
        <Form form={createRiskForm} layout="vertical">
          <Row gutter={16}>
             <Col xs={24} md={12}>
                <Form.Item 
                  name="risk_point_name" 
                  label="风险点名称" 
                  rules={[{ required: true, message: '请输入风险点名称' }]}
                >
                  <Input placeholder="如：酸洗线酸液喷溅" />
                </Form.Item>
             </Col>
             <Col xs={24} md={12}>
                <Form.Item 
                  name="department" 
                  label="所在部门" 
                  rules={[{ required: true, message: '请输入部门' }]}
                >
                  <Input placeholder="如：生产部/酸洗车间" />
                </Form.Item>
             </Col>
          </Row>

          <Row gutter={16}>
             <Col xs={24} md={12}>
                <Form.Item 
                  name="identified_by" 
                  label="识别人" 
                  rules={[{ required: true, message: '请输入识别人' }]}
                >
                  <Input placeholder="默认当前用户" />
                </Form.Item>
             </Col>
             <Col xs={24} md={12}>
                <Form.Item 
                  name="identification_date" 
                  label="识别日期" 
                  rules={[{ required: true, message: '请选择日期' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
             </Col>
          </Row>

          <Form.Item 
             name="identification_methods" 
             label="识别方法（可多选）" 
             rules={[{ required: true, message: '请选择识别方法' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="请选择" 
              options={TOOL_MATRIX.map(t => ({ label: t.primary_name, value: t.primary_name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增/编辑条目 */}
      <Modal
        title={itemEditing?.id ? '编辑危险源/风险事件' : '新增危险源/风险事件'}
        open={itemModalOpen}
        onCancel={() => setItemModalOpen(false)}
        onOk={submitItem}
        width={modalWidthMd}
      >
        <Form form={itemForm} layout="vertical">
          <Form.Item name="risk_point_id" label="归属风险点" rules={[{ required: true, message: '请选择风险点' }]}>
            <Select 
              placeholder="请选择" 
              options={riskPoints.map(r => ({ label: r.risk_point_name, value: r.id }))} 
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              showSearch
            />
          </Form.Item>
          
          <Row gutter={16}>
             <Col xs={24} md={12}>
                <Form.Item name="likelihood_level" label="可能性(L) 1-5" rules={[{ required: true, message: '请选择' }]}>
                  <Select options={[1,2,3,4,5].map(v => ({ label: `${v}`, value: v }))} />
                </Form.Item>
             </Col>
             <Col xs={24} md={12}>
                <Form.Item name="loss_level" label="损失程度(S) 1-5" rules={[{ required: true, message: '请选择' }]}>
                  <Select options={[1,2,3,4,5].map(v => ({ label: `${v}`, value: v }))} />
                </Form.Item>
             </Col>
          </Row>

          <Form.Item shouldUpdate>
            {() => {
               const l = itemForm.getFieldValue('likelihood_level');
               const s = itemForm.getFieldValue('loss_level');
               const { riskValue, riskLevel } = computeMatrixRisk(l, s);
               return (
                 <div style={{ marginBottom: 16, background: '#f5f5f5', padding: '8px 12px', borderRadius: 4 }}>
                    <Space>
                       <span>风险值(R): <b>{riskValue || '-'}</b></span>
                       <Divider type="vertical" />
                       <span>风险等级: {riskLevelTag(riskLevel)}</span>
                    </Space>
                 </div>
               );
            }}
          </Form.Item>

          <Form.Item
            name="hazard_source_category"
            label="危险源类别"
            rules={[{ required: true, message: '请选择危险源类别' }]}
          >
            <Cascader options={HAZARD_SOURCE_CATEGORY_OPTIONS} placeholder="请选择（按内置分类）" allowClear />
          </Form.Item>
          <Form.Item name="hazard_source_name" label="安全风险事件/危险源名称" rules={[{ required: true, message: '请输入名称' }]}>
            <TextArea rows={2} placeholder="如：酸洗线酸液喷溅" />
          </Form.Item>
          <Form.Item name="possible_consequence" label="可能后果（可选）">
            <TextArea rows={2} placeholder="如：灼伤/触电/坠落/火灾等" />
          </Form.Item>
          <Form.Item name="control_measures" label="控制措施（支持检索/多选）">
             <Select
                mode="tags"
                placeholder="输入或选择管控措施"
                options={measureOptions}
                optionFilterProp="label"
                showSearch
             />
          </Form.Item>
          <Form.Item name="category_id" label="自定义类别（可选）">
            <Select allowClear options={categoryOptions} placeholder="请选择" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 合并 */}
      <Modal
        title="合并危险源"
        open={mergeModalOpen}
        onCancel={() => setMergeModalOpen(false)}
        onOk={submitMerge}
        okText="合并"
      >
        <Text type="secondary">将所选条目合并为一条新的危险源（原条目会标记为已合并）。</Text>
        <Divider />
        <Form form={mergeForm} layout="vertical">
          <Form.Item name="risk_point_id" hidden rules={[{ required: true, message: '缺少风险点' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="risk_point_name" label="归属风险点">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="hazard_source_category"
            label="危险源类别"
            rules={[{ required: true, message: '请选择危险源类别' }]}
          >
            <Cascader options={HAZARD_SOURCE_CATEGORY_OPTIONS} placeholder="请选择（按内置分类）" allowClear />
          </Form.Item>
          <Form.Item name="hazard_source_name" label="危险源名称" rules={[{ required: true, message: '请输入危险源名称' }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="possible_consequence" label="可能后果（可选）">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="control_measures" label="控制措施（可选）">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="category_id" label="自定义类别（可选）">
            <Select allowClear options={categoryOptions} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 评审 */}
      <Modal
        title={reviewAction === 'APPROVE' ? '评审通过' : '评审驳回'}
        open={reviewModalOpen}
        onCancel={() => setReviewModalOpen(false)}
        onOk={submitReview}
        okText="提交"
      >
        <Form form={reviewForm} layout="vertical">
          <Form.Item name="comment" label="评审意见（可选）">
            <TextArea rows={3} placeholder="可填写评审结论/修改建议" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 分级 */}
      <Modal
        title="风险分级（矩阵）"
        open={gradeModalOpen}
        onCancel={() => setGradeModalOpen(false)}
        onOk={submitGrade}
        okText="应用"
        width={isMobile ? '96%' : undefined}
      >
        <Form form={gradeForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="likelihood_level" label="发生可能性(1-5)" rules={[{ required: true, message: '必填' }]}>
                <InputNumber min={1} max={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="loss_level" label="损失程度(1-5)" rules={[{ required: true, message: '必填' }]}>
                <InputNumber min={1} max={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item shouldUpdate>
            {() => {
              const l = gradeForm.getFieldValue('likelihood_level');
              const s = gradeForm.getFieldValue('loss_level');
              const { riskValue, riskLevel } = computeMatrixRisk(l, s);
              return (
                <Text type="secondary">
                  预览：风险值 {riskValue ?? '—'}，等级 {riskLevel ? riskLevelTag(riskLevel) : '—'}
                </Text>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* 建档 */}
      <Modal
        title="建档为“隐患风险记录”"
        open={archiveModalOpen}
        onCancel={() => setArchiveModalOpen(false)}
        onOk={submitArchive}
        okText="建档"
      >
        <Text type="secondary">建档后将写入“风险分级 → 隐患风险记录”，可用于后续隐患排查引用。</Text>
        <Divider />
        <Form form={archiveForm} layout="vertical">
          <Form.Item name="inspection_cycle" label="检查周期（可选）">
            <Input placeholder="如：每日/每周/每月" />
          </Form.Item>
          <Form.Item name="inspection_responsible" label="检查责任人（可选）">
            <Input placeholder="如：张三" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 管控措施库弹窗 */}
      <Modal
        title="制定管控措施"
        open={measureModalOpen}
        onCancel={() => setMeasureModalOpen(false)}
        footer={[<Button key="close" onClick={() => setMeasureModalOpen(false)}>关闭</Button>]} 
        width={modalWidthLg}
      >
        <Card size="small" title="新增措施" style={{marginBottom: 16}}>
            <Form form={measureForm} layout="vertical" onFinish={submitMeasure}>
                <Row gutter={12}>
                  <Col xs={24} md={10}>
                         <Form.Item name="name" label="措施名称/内容" rules={[{ required: true, message: '必填' }]}>
                             <Input placeholder="如：加装紧急切断装置"/>
                         </Form.Item>
                    </Col>
                  <Col xs={24} md={8}>
                        <Form.Item name="measure_type" label="类型（优先顺序）" rules={[{ required: true, message: '请选择类型' }]}>
                            <Select options={MEASURE_TYPE_OPTIONS} placeholder="请选择" />
                        </Form.Item>
                    </Col>
                  <Col xs={24} md={6}>
                         <Form.Item label=" " colon={false}>
                             <Button type="primary" htmlType="submit" block icon={<PlusOutlined />}>添加</Button>
                         </Form.Item>
                    </Col>
                </Row>
                 <Form.Item name="description" label="详细描述（可选）" style={{marginBottom: 0}}>
                      <Input.TextArea rows={1} placeholder="补充说明"/>
                 </Form.Item>
            </Form>
        </Card>
        
        <Text strong>已有措施库</Text>
        <Table 
            style={{marginTop: 8}}
            size="small"
            rowKey="id"
            scroll={tableScrollX}
            columns={[
                { 
                  title: '类型', 
                  dataIndex: 'measure_type', 
                  width: 130, 
                  render: (v) => v ? <Tag>{MEASURE_TYPE_LABELS[v] || v}</Tag> : <Tag>通用</Tag>,
                  filters: MEASURE_TYPE_OPTIONS.map(o => ({ text: o.label, value: o.value })),
                  onFilter: (value, record) => record.measure_type === value,
                },
                { title: '措施名称', dataIndex: 'name' },
                { title: '详细描述', dataIndex: 'description', ellipsis: true, responsive: ['md'] },
            ]}
            dataSource={measuresList}
            pagination={{ pageSize: 5 }}
        />
      </Modal>
    </div>
  );
}
